import { Skeleton } from './Skeleton'

function MetricRow({ icon, label, value, sub, accent }) {
  return (
    <div className={`flex items-center justify-between py-3 border-b-2 border-on-background/8 last:border-0`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 border-2 border-on-background flex items-center justify-center shrink-0 ${accent ? 'bg-primary-container' : 'bg-surface-container'}`}>
          <span className="material-symbols-outlined text-[15px]" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>
        </div>
        <div>
          <p className="font-bold text-[12px] uppercase tracking-wider">{label}</p>
          {sub && <p className="text-[10px] text-on-surface-variant">{sub}</p>}
        </div>
      </div>
      <p className={`font-extrabold text-[22px] leading-none ${accent ? 'text-primary-container' : ''}`}>{value ?? '—'}</p>
    </div>
  )
}

function MiniBar({ value, max, color = 'bg-primary-container' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-surface-container border border-on-background/10 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-[10px] font-bold w-8 text-right">{pct}%</span>
    </div>
  )
}

export function PlatformMetrics({ stats, loading }) {
  const dailyNewUsers  = stats?.dailyNewUsers  ?? '—'
  const dailyPosts     = stats?.dailyPosts     ?? '—'
  const dailyActiveUsers = stats?.dailyActiveUsers ?? '—'
  const totalUsers     = stats?.totalUsers     ?? '—'
  const totalPosts     = stats?.totalPosts     ?? '—'
  const weeklySignups  = stats?.weeklySignups  ?? '—'
  const weeklyPosts    = stats?.weeklyPosts    ?? '—'
  const retentionRate  = stats?.retentionRate  ?? '—'
  const engagementRate = stats?.engagementRate ?? '—'

  const maxBar = Math.max(
    typeof dailyNewUsers === 'number' ? dailyNewUsers : 0,
    typeof dailyPosts === 'number' ? dailyPosts : 0,
    typeof dailyActiveUsers === 'number' ? dailyActiveUsers : 0,
    1
  )

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-64" />)}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary-container text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>bar_chart</span>
          <h2 className="font-extrabold text-[15px] uppercase tracking-tight">Platform Activity Metrics</h2>
        </div>
        <span className="font-bold text-[9px] uppercase tracking-widest text-on-surface-variant border-2 border-on-background/15 px-2 py-1">Live</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Daily metrics */}
        <div className="bg-surface border-4 border-on-background p-5 flex flex-col gap-1" style={{ boxShadow: '4px 4px 0px 0px #d4af37' }}>
          <p className="font-extrabold text-[11px] uppercase tracking-widest text-primary-container mb-2">Today</p>

          <MetricRow icon="person_add"   label="New Registrations"   value={dailyNewUsers}     sub="Signups in last 24h"       accent />
          <MetricRow icon="edit_note"    label="Posts Created"        value={dailyPosts}        sub="Posts published today"            />
          <MetricRow icon="group"        label="Daily Active Users"   value={dailyActiveUsers}  sub="Unique sessions today"    accent />

          {/* Visual bars */}
          <div className="mt-3 flex flex-col gap-2 pt-3 border-t-2 border-on-background/10">
            <p className="font-bold text-[9px] uppercase tracking-widest text-on-surface-variant mb-1">Activity distribution</p>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                <span className="w-20 shrink-0 text-on-surface-variant">New users</span>
                <MiniBar value={typeof dailyNewUsers === 'number' ? dailyNewUsers : 0} max={maxBar} color="bg-primary-container" />
              </div>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                <span className="w-20 shrink-0 text-on-surface-variant">Posts</span>
                <MiniBar value={typeof dailyPosts === 'number' ? dailyPosts : 0} max={maxBar} color="bg-blue-400" />
              </div>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                <span className="w-20 shrink-0 text-on-surface-variant">Active</span>
                <MiniBar value={typeof dailyActiveUsers === 'number' ? dailyActiveUsers : 0} max={maxBar} color="bg-secondary-container" />
              </div>
            </div>
          </div>
        </div>

        {/* Overall platform metrics */}
        <div className="bg-surface border-4 border-on-background p-5 flex flex-col gap-1" style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
          <p className="font-extrabold text-[11px] uppercase tracking-widest text-primary-container mb-2">Overall Platform</p>

          <MetricRow icon="group"         label="Total Users"         value={totalUsers}       sub="All registered accounts"        />
          <MetricRow icon="article"       label="Total Posts"         value={totalPosts}       sub="All posts ever created"   accent />
          <MetricRow icon="event_note"    label="Weekly Signups"      value={weeklySignups}    sub="New users this week"             />
          <MetricRow icon="trending_up"   label="Weekly Posts"        value={weeklyPosts}      sub="Posts this week"          accent />
          <MetricRow icon="repeat"        label="Retention Rate"      value={retentionRate !== '—' ? `${retentionRate}%` : '—'} sub="Return visit rate" />
          <MetricRow icon="thumb_up"      label="Engagement Rate"     value={engagementRate !== '—' ? `${engagementRate}%` : '—'} sub="Likes + comments per post" />
        </div>
      </div>
    </div>
  )
}
